import type { ProfileLook, ProfileLooks } from "@/lib/content/types";

/* A player's look: the profile image and profile background they chose from
   the site's catalogue (/admin -> Profile images). The platform keeps no such
   field, so the casino keeps it: one record per account id, written only by
   the signed-in player it belongs to (api/profile/look). A choice is stored
   as the item's image URL, and only counts while that item is still in the
   catalogue and unlocked, so a removed or replaced picture falls back to the
   default rather than showing something the operator took away. */

export type PlayerLook = { avatar: string | null; background: string | null };

export const EMPTY_LOOK: PlayerLook = { avatar: null, background: null };

const available = (items: ProfileLook[], url: string | null | undefined) =>
  Boolean(url) && items.some((item) => item.imageUrl === url && !item.locked);

/* What a stored record still means against today's catalogue. */
export const resolveLook = (stored: Partial<PlayerLook> | null | undefined, catalogue: ProfileLooks): PlayerLook => ({
  avatar: available(catalogue.avatars, stored?.avatar) ? (stored?.avatar ?? null) : null,
  background: available(catalogue.backgrounds, stored?.background) ? (stored?.background ?? null) : null,
});

/* A change a player asks for: each field is left alone (undefined), cleared
   (null), or set to an unlocked catalogue item. Anything else is refused. */
export const validateLookChange = (
  body: unknown,
  catalogue: ProfileLooks,
): { ok: true; change: Partial<PlayerLook> } | { ok: false; reason: string } => {
  if (!body || typeof body !== "object" || Array.isArray(body)) return { ok: false, reason: "Expected an object." };

  const change: Partial<PlayerLook> = {};
  const record = body as Record<string, unknown>;

  for (const [field, items] of [
    ["avatar", catalogue.avatars],
    ["background", catalogue.backgrounds],
  ] as const) {
    if (!(field in record) || record[field] === undefined) continue;
    const value = record[field];
    if (value === null) {
      change[field] = null;
      continue;
    }
    if (typeof value !== "string" || !available(items, value)) {
      return { ok: false, reason: `That ${field === "avatar" ? "profile image" : "background"} is not available.` };
    }
    change[field] = value;
  }

  return { ok: true, change };
};

export const parseStoredLook = (raw: string | null): Partial<PlayerLook> | null => {
  if (!raw) return null;
  try {
    const value = JSON.parse(raw) as unknown;
    return value && typeof value === "object" ? (value as Partial<PlayerLook>) : null;
  } catch {
    return null;
  }
};
